/**
 * COVER ART — a song's artwork sized to fill the box it is given.
 *
 * A thin adapter over components/songs/SongArtwork, which owns the actual rule
 * (uploaded image beats seeded gradient) shared with the library. This exists
 * separately because the Studio's two artwork boxes — the ACTIVE SONG panel and the
 * Cover Art preview — are sized by their containers rather than by a pixel height,
 * so they want `width/height: 100%` and a radius, not the `height` prop the library
 * thumbnails pass. Collapsing the two would mean every library caller passing
 * fill styles it does not want.
 *
 * Always renders something. It used to return null when a song had no artwork, which
 * made each caller invent its own empty state and let the library and the Studio
 * disagree about whether a song had a cover at all. The gradient IS the default
 * state; "has the user applied artwork?" is hasCoverArt() from lib/artwork, which is
 * a different question and still asked where it matters.
 */
import type { CSSProperties } from 'react';
import { SongArtwork } from '../songs';
import type { ArtworkDepth } from '../songs';
import type { Song } from '../../types/domain';

export interface CoverArtProps {
  song: Song;
  /** Gradient depth, passed through to ArtworkPlaceholder. */
  depth?: ArtworkDepth;
  /** Corner radius, in px. Matches whatever box it fills. */
  radius?: number;
  style?: CSSProperties;
}

export default function CoverArt({ song, depth = 'hero', radius = 8, style }: CoverArtProps) {
  return (
    <SongArtwork
      song={song}
      depth={depth}
      radius={radius}
      style={{ width: '100%', height: '100%', ...style }}
    />
  );
}
